import {
  Component, DestroyRef,
  EventEmitter,
  HostListener, inject,
  Input,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import { LOREN_ROUTES } from "../../../../../../constants/loren-routes.constants";
import { LOREN_USER_ROLES } from "../../../../../../services/loren-authentication-service/constants/loren-user-roles.constants";
import { LOREN_MENU_ITEMS } from "./constants/loren-menu-items.constants";
import { LorenMenuItemType } from "../../enums/loren-menu-item-type.enum";
import { LorenMenuItem } from "../../interfaces/loren-menu-item.interface";
import { LorenAdminChannel } from "../../../../../../../features/loren-admin/shared/interfaces/loren-admin-channel.interface";
import { LorenAdminChannelsService } from "../../../../../../../features/loren-admin/shared/services/loren-admin-channels-service/loren-admin-channels.service";
import { LorenBreakpointObserverService } from "../../../../../../services/loren-breakpoint-observer-service/loren-breakpoint-observer.service";
import { LorenDetailsOutletService } from "../../../../../../services/loren-details-outlet-service/loren-details-outlet.service";
import { LorenNavService } from "../../services/loren-nav-service/loren-nav.service";
import { LorenFeatureToggleService } from "../../../../../../services/loren-feature-toggle-service/loren-feature-toggle.service";
import {forkJoin, Observable, of} from "rxjs";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import { whitelabelConfig } from "../../../../../../../../whitelabel/config";
import {map, switchMap} from "rxjs/operators";

@Component({
  selector: "loren-sidenav",
  templateUrl: "./loren-sidenav.component.html",
  styleUrls: ["./loren-sidenav.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LorenSidenavComponent implements OnInit {
  private lorenNavService = inject(LorenNavService);
  private detailsOutletService = inject(LorenDetailsOutletService);
  private breakpointObserverService = inject(LorenBreakpointObserverService);
  private lorenAdminChannelsService = inject(LorenAdminChannelsService);
  private featureToggleService = inject(LorenFeatureToggleService);

  private readonly destroyRef = inject(DestroyRef);

  @Input() isHandset: boolean;
  @Input() collapsed: boolean;

  @Output() navMenuClicked = new EventEmitter<Event>();
  @Output() toggleClicked = new EventEmitter<null>();

  readonly routes = LOREN_ROUTES;
  menuItems = this.lorenNavService.inAdminApp$
      .pipe(
          takeUntilDestroyed(this.destroyRef),
          switchMap((inAdminApp) => {
            if (inAdminApp) {
              return of(LOREN_MENU_ITEMS.ADMIN);
            } else {
              return this.filterMenuItemsByFeatures();
            }
          }),
      );
  menuItemTypes = LorenMenuItemType;
  channels: LorenAdminChannel[] = [];
  currentChannel: number;
  roles = LOREN_USER_ROLES;
  shouldStick: boolean;
  inAdminApp = toSignal(this.lorenNavService.inAdminApp$);
  disableChannelSelector$ = this.lorenNavService.routerUrl$
      .pipe(
          takeUntilDestroyed(this.destroyRef),
          map((url) => {
            return url.endsWith(LOREN_ROUTES.DEFINE) ||
                url.endsWith(LOREN_ROUTES.EDIT) ||
                url.endsWith(LOREN_ROUTES.DELETE);
          })
      );
  showPoweredBy = whitelabelConfig.showPoweredBy;

  constructor() {
    this.handleScreenHeight();
  }

  ngOnInit() {
    this.setUpSubscriptions();
  }

  handleClick($event: MouseEvent) {
    if (this.detailsOutletService.getLastState()?.visible) {
      setTimeout(() => {
        this.detailsOutletService.resetDetailsOutlet();
        this.detailsOutletService.toggleState(false);
      }, 500);
    }

    if (this.isHandset) {
      this.navMenuClicked.emit($event);
    }
  }

  channelChanged(channelId: number) {
    this.currentChannel = channelId;
    this.lorenAdminChannelsService.setCurrentChannel(channelId);
  }

  clickToggle() {
    this.toggleClicked.emit();
  }

  private setUpSubscriptions() {
    // TODO: Refactor this
    this.lorenAdminChannelsService
        .getChannels()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((channels) => {
          this.channels = channels;
          this.currentChannel = this.lorenAdminChannelsService.getCurrentChannel();
        });


    this.breakpointObserverService
      .getHandsetStateSubscription()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => (this.isHandset = res));
  }

  private filterMenuItemsByFeatures(): Observable<LorenMenuItem[]> {
    const allMenuItems = LOREN_MENU_ITEMS.APP;
    const featureChecks: Observable<boolean>[] = [];
    const menuItemsWithFeatures: LorenMenuItem[] = [];

    // Separate menu items with and without feature toggles
    allMenuItems.forEach((menuItem) => {
      if (menuItem.featureToggle) {
        menuItemsWithFeatures.push(menuItem);
        featureChecks.push(
          this.featureToggleService.userHasFeature(menuItem.featureToggle)
        );
      }
    });

    if (featureChecks.length === 0) {
      return of(allMenuItems);
    }

    return forkJoin(featureChecks)
      .pipe(
          takeUntilDestroyed(this.destroyRef),
          map((featureResults) => {
            const filteredMenuItems: LorenMenuItem[] = [];

            const menuItemsWithFeaturesMap = new Map();
            featureResults.forEach((hasFeature, index) => {
              menuItemsWithFeaturesMap.set(menuItemsWithFeatures[index], hasFeature);
            });

            allMenuItems.forEach((menuItem) => {
              if (menuItem.featureToggle) {
                if (menuItemsWithFeaturesMap.get(menuItem)) {
                  filteredMenuItems.push(menuItem);
                }
              } else {
                filteredMenuItems.push(menuItem);
              }
            });

            return filteredMenuItems;
          })
      );
  }

  @HostListener("window:resize")
  handleScreenHeight() {
    this.shouldStick = window.innerHeight <= 650;
  }
}
